import type { GetLocationVehiclesRequest, LocationVehiclesRequest, VehicleCategoryData } from '~/api/prebooking'
import type { City } from '~/composables/city'
import * as Sentry from '@sentry/vue'
import { useDebounceFn } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ExternalControllerApi, VehicleCategoryDataVehicleCategoryEnum } from '~/api/prebooking'
import { toRentalDay } from '~/composables/prebooking-date'
import { useDateTimeStore } from '~/stores/datetime'
import { useLocationStore } from '~/stores/location'
import { useOfferStore } from '~/stores/offer'

const prebookingApi = new ExternalControllerApi()

export const useVehiclesStore = defineStore('vehicles', () => {
  const locationStore = useLocationStore()
  const vehiclesConfigurations = reactive<Map<VehicleCategoryDataVehicleCategoryEnum, VehicleCategoryData>>(new Map())
  const buildSeriesToCategory = reactive<Map<string, VehicleCategoryDataVehicleCategoryEnum>>(new Map())

  function loadVehicleCategoryData(city: City, startDate?: Date) {
    if (startDate) {
      // Serialised as `toISOString().substring(0, 10)`, so hand it the rental calendar day
      // rather than the wall-clock instant — otherwise early pick-ups report the day before.
      const locationVehiclesRequest: LocationVehiclesRequest = { startDate: toRentalDay(startDate) }
      const request: GetLocationVehiclesRequest = { locationId: city.locationId, locationVehiclesRequest }

      prebookingApi.getLocationVehicles(request)
        .then((vehicleCategory: VehicleCategoryData[]) => {
          vehiclesConfigurations.clear()
          buildSeriesToCategory.clear()
          if (vehicleCategory.length === 0) {
            // TODO: user notification?
            Sentry.captureMessage(`Location ${city?.locationId} does not have any vehicle configuration`)
          }
          vehicleCategory.forEach((conf: VehicleCategoryData) => {
            vehiclesConfigurations.set(conf.vehicleCategory, { ...conf })
            buildSeriesToCategory.set(conf.buildSeries[0], conf.vehicleCategory)
          })
        })
        .catch((error) => {
          // TODO proper error handling (Notif?)
          Sentry.captureMessage(`Error while fetching the vehicle configurations address: ${error}`)
        })
    }
    else {
      // console.debug(`Start date not defined yet`)
    }
  }
  const debouncedLoadConfig = useDebounceFn(loadVehicleCategoryData, 200)

  watchEffect(async () => {
    await debouncedLoadConfig(locationStore.city, useDateTimeStore().startDate.date)
  })

  function getCategory(buildSeries?: string): VehicleCategoryDataVehicleCategoryEnum | undefined {
    const bs = buildSeries ?? selectedBuildSeries()
    return buildSeriesToCategory.get(bs!)
  }

  function getAllBuildSeries(): string[] {
    return Array.from(buildSeriesToCategory.keys())
  }

  function getBuildSeries(category?: string): string | undefined {
    if (!category || !(category in VehicleCategoryDataVehicleCategoryEnum))
      return undefined

    return vehiclesConfigurations.get(category as VehicleCategoryDataVehicleCategoryEnum)?.buildSeries?.[0] ?? undefined
  }

  function getLinkedBuildSeries(buildSeries?: string): string[] {
    const bs = buildSeries ?? selectedBuildSeries()
    if (!bs || !buildSeriesToCategory.has(bs))
      return []

    const category = buildSeriesToCategory.get(bs)!
    return vehiclesConfigurations.get(category)?.buildSeries ?? []
  }

  function getVehicleCategoryData(buildSeries?: string): VehicleCategoryData | undefined {
    const bs = buildSeries ?? selectedBuildSeries()
    const category = buildSeriesToCategory.get(bs!)
    return category ? vehiclesConfigurations.get(category) : undefined
  }

  function selectedBuildSeries() {
    return useOfferStore().selectedOffer?.buildSeries
  }

  return {
    getCategory,
    getAllBuildSeries,
    getBuildSeries,
    getLinkedBuildSeries,
    getVehicleCategoryData,
  }
})
